import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {withStringFilterProperties, withDateFilterProperties} from 'Shared/components/PagedView/Filter';
import './EnrollmentFileRejectsEmailHistory.sass';
import {
  getEnrollmentFileRejectsEmailHistory,
  changeEnrollmentFileRejectEmailHistoryFilters,
} from 'Enrollment/ducks/enrollmentFileProcessingReject';
import {toShortDateTimeWithSeconds} from 'Shared/utilities/DateFormatter';
import * as E from 'fp-ts/lib/Either';
import {safeParseInt} from 'Shared/utilities/parsers';
import {pipe} from 'fp-ts/lib/pipeable';
import {FileExtension} from 'Shared/components/FileDownloadLink';
import PagedView from 'Shared/components/PagedView/PagedView';
import ToolbarHeader, {ExportToolbarHeaderIcon} from 'Shared/components/Toolbar/ToolbarHeader';
import EnrollmentFileRejectsEmailHistoryContentDialog from './EnrollmentFileRejectsEmailHistoryContentDialog';
import {toPageEither} from 'Core/models/Async';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {MenuItem} from '@material-ui/core';

const EnrollmentFileRejectsEmailHistory = () => {
  const dispatch = useDispatch();
  const {enrollmentFileRejectEmailHistory, enrollmentFileRejectEmailHistoryFilter} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessingReject
  );
  const {id : applicationId, applicationSource} = useParams();
  const changeFilter = withValueChangedHook(
    enrollmentFileRejectEmailHistoryFilter,
    changeEnrollmentFileRejectEmailHistoryFilters,
    dispatch
  );
  const filterProperties = {
    changeFilter,
    filterData: enrollmentFileRejectEmailHistoryFilter,
  };
  const [openErrorBox, setOpenErrorBox] = useState(false);
  const [id, setId] = useState<string | null | undefined>(null);
  const [attachment, setAttachment] = useState<string | null | undefined>(null);
  const [attachmentName, setAttachmentName] = useState<string | null | undefined>(null);
  const handleClose = () => {
    setOpenErrorBox(false);
  };

  useEffect(() => {
    dispatch(
      getEnrollmentFileRejectsEmailHistory(applicationId, applicationSource, undefined, enrollmentFileRejectEmailHistoryFilter)
    );
  }, [dispatch, enrollmentFileRejectEmailHistoryFilter, applicationId, applicationSource]);

  useEffect(() => {
    dispatch(
      getEnrollmentFileRejectsEmailHistory(applicationId, applicationSource, undefined, enrollmentFileRejectEmailHistoryFilter)
    );
  }, [dispatch, enrollmentFileRejectEmailHistoryFilter,applicationId]);

  const emailHistories = pipe(
    toPageEither(enrollmentFileRejectEmailHistory),
    E.fold(
      () => undefined,
      value => value.result
    )
  )!;

  return (
    <>
      <EnrollmentFileRejectsEmailHistoryContentDialog
        dialogOpen={openErrorBox}
        dialogClose={handleClose}
        emailHistories={emailHistories}
        applicationId={applicationId!}
        attachment={attachment}
        attachmentName={attachmentName}
        id={id}
        applicationSource={applicationSource!}
      />
      <ModularHost>
        <ToolbarHeader title="E-mail History">
          <ExportToolbarHeaderIcon
            title="Export History"
            downloadedFileName={`E-mail_History_${applicationId}`}
            extension={FileExtension.CSV}
            relativeFileLocation={`/enrollmentFiles/processingHistory/rejects/${applicationId}/email/export/${applicationSource!}/${FileExtension.CSV.toLowerCase().substring(
              1
            )}`}
            totalRecordCount = {0}
          />
        </ToolbarHeader>
        <PagedView
          pagedViewKey="enrollmentFileRejectHistory"
          data={enrollmentFileRejectEmailHistory}
          loadMore={page =>
            dispatch(
              getEnrollmentFileRejectsEmailHistory(applicationId, applicationSource, page, enrollmentFileRejectEmailHistoryFilter)
            )
          }
        >
          {columnProps => (
            <>
              <Column
                {...columnProps}
                title="Subject"
                filterProperties={withStringFilterProperties(filterProperties, 'subject')}
                default
              >
                {enrollmentFileRejectEmailHistory => enrollmentFileRejectEmailHistory.subject!}
              </Column>
              <Column
                {...columnProps}
                title="Sent Date"
                filterProperties={withDateFilterProperties(filterProperties, 'sentDate')}
                default
              >
                {enrollmentFileRejectEmailHistory =>
                  toShortDateTimeWithSeconds(enrollmentFileRejectEmailHistory.sentDate!)
                }
              </Column>
              <Column
                {...columnProps}
                title="Sent To"
                filterProperties={withStringFilterProperties(filterProperties, 'to')}
                default
              >
                {enrollmentFileRejectEmailHistory => {
                  const to = enrollmentFileRejectEmailHistory.to!;

                  if (to.length > 1) {
                    return 'Multiple';
                  }
                  else if (to.length === 1) {
                    return to[0].email!;
                  }
                }}
              </Column>
              <Column
                {...columnProps}
                title="CC"
                filterProperties={withStringFilterProperties(filterProperties, 'cc')}
                default
              >
                {enrollmentFileRejectEmailHistory => {
                  const cc = enrollmentFileRejectEmailHistory.cc!;
                  if (cc && cc.length > 1) {
                    return 'Multiple';
                  }
                  else if (cc && cc.length === 1) {
                    return cc[0].email!;
                  }
                }}
              </Column>
              <Column
                {...columnProps}
                title="Sent By"
                filterProperties={withStringFilterProperties(filterProperties, 'sentBy')}
                default
              >
                {enrollmentFileRejectEmailHistory => {
                  let firstName = enrollmentFileRejectEmailHistory.firstName!;
                  let lastName = enrollmentFileRejectEmailHistory.lastName!;
                  let username = enrollmentFileRejectEmailHistory.username!;

                  if (firstName && firstName !== null) {
                    firstName = `${enrollmentFileRejectEmailHistory.firstName!} `;
                  } else {
                    firstName = '';
                  }

                  if (lastName && lastName !== null) {
                    lastName = `${enrollmentFileRejectEmailHistory.lastName!} `;
                  } else {
                    lastName = '';
                  }

                  if (username && username !== null) {
                    username = `(${enrollmentFileRejectEmailHistory.username!})`;
                  } else {
                    username = '';
                  }

                  return `${firstName}${lastName}${username}`;
                }}
              </Column>
              <Column {...columnProps} title="Actions" isActions default>
                {enrollmentFileRejectEmailHistory => (
                  <>
                    <ActionsMenu>
                      <MenuItem
                        onClick={() => {
                          setId(enrollmentFileRejectEmailHistory.id!);
                          setAttachment(enrollmentFileRejectEmailHistory.attachment!);
                          setAttachmentName(enrollmentFileRejectEmailHistory.attachmentName!);
                          setOpenErrorBox(true);
                        }}
                      >
                        Details
                      </MenuItem>
                    </ActionsMenu>
                  </>
                )}
              </Column>
            </>
          )}
        </PagedView>
      </ModularHost>
    </>
  );
};

export default EnrollmentFileRejectsEmailHistory;
